import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Building2,
  Briefcase,
  Layers,
  LifeBuoy,
  FileBarChart2,
  RefreshCw,
  Truck,
  ClipboardList,
  Shield,
  CreditCard,
  Sparkles,
  Users as UsersIcon,
  Target as TargetIcon,
  TrendingUp,
  ChevronDown,
  Settings as SettingsIcon,
  Tv,
  Wrench,
  Megaphone,
  UserPlus,
  KeyRound,
  Flame,
  ShieldAlert,
  X as XIcon,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { XentraIcon } from "@/components/XentraLogo";

// Grouped sidebar navigation per Xentra board-tracker spec.
const groups = [
  {
    items: [
      { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
      { to: "/app/dashboard?tab=budget", label: "Budget Tracker", icon: TrendingUp, testid: "nav-budget-tracker", matchPath: "/app/dashboard" },
    ],
  },
  {
    title: "Growth",
    items: [
      { to: "/app/leads", label: "Leads", icon: TargetIcon, testid: "nav-leads" },
      { to: "/app/outbound", label: "Outbound", icon: Flame, testid: "nav-outbound" },
      { to: "/app/accounts", label: "Accounts", icon: Building2, testid: "nav-accounts" },
      { to: "/app/deals", label: "Deals", icon: Briefcase, testid: "nav-deals" },
      { to: "/app/renewals", label: "Renewals", icon: RefreshCw, testid: "nav-renewals" },
      { to: "/app/sales-tools", label: "Sales Tools", icon: Sparkles, testid: "nav-sales-tools" },
    ],
  },
  {
    title: "Technical",
    items: [
      { to: "/app/projects", label: "Projects", icon: ClipboardList, testid: "nav-projects" },
      { to: "/app/licence-usage", label: "Licence Usage", icon: KeyRound, testid: "nav-licence-usage" },
    ],
  },
  {
    title: "Marketing",
    items: [
      { to: "/app/marketing", label: "Marketing", icon: Megaphone, testid: "nav-marketing" },
    ],
  },
  {
    title: "Support",
    items: [
      { to: "/app/tickets", label: "Tickets", icon: LifeBuoy, testid: "nav-tickets" },
      { to: "/app/threat-advisories", label: "Threat Advisories", icon: ShieldAlert, testid: "nav-threat-advisories" },
      { to: "/app/support/config", label: "Configuration", icon: SettingsIcon, testid: "nav-support-config", adminOnly: true },
    ],
  },
  {
    title: "Operations",
    items: [
      { to: "/app/stax", label: "Stax Catalog", icon: Layers, testid: "nav-stax" },
      { to: "/app/vendors", label: "Vendors", icon: Truck, testid: "nav-vendors" },
      { to: "/app/people", label: "People", icon: UsersIcon, testid: "nav-people" },
      { to: "/app/onboarding", label: "On-boarding", icon: UserPlus, testid: "nav-onboarding" },
      { to: "/app/billing", label: "Billing", icon: CreditCard, testid: "nav-billing" },
      { to: "/app/reports", label: "Reports", icon: FileBarChart2, testid: "nav-reports" },
      { to: "/app/reports/technical", label: "Technical Reports", icon: Wrench, testid: "nav-reports-technical" },
    ],
  },
];

const adminItems = [
  { to: "/app/admin", label: "Admin", icon: Shield, testid: "nav-admin" },
  { to: "/app/admin/wall-display", label: "Wall display", icon: Tv, testid: "nav-wall-display" },
];

const STORAGE_KEY = "xentra.sidebar.openGroups";

export default function Sidebar({ mobileOpen = false, onCloseMobile }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const location = useLocation();

  // Persisted open/closed state per group title. Default: every group OPEN on first load.
  const [openMap, setOpenMap] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return JSON.parse(raw);
    } catch (_) { /* ignore */ }
    return Object.fromEntries(groups.filter((g) => g.title).map((g) => [g.title, true]));
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(openMap)); }
    catch (_) { /* ignore */ }
  }, [openMap]);

  // If the user is currently on a route that belongs to a collapsed group,
  // auto-open that group so they can see where they are.
  useEffect(() => {
    const path = location.pathname;
    for (const g of groups) {
      if (!g.title) continue;
      const matches = g.items.some((it) => path.startsWith(it.to.split("?")[0]));
      if (matches && !openMap[g.title]) {
        setOpenMap((m) => ({ ...m, [g.title]: true }));
        break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggle = (title) => setOpenMap((m) => ({ ...m, [title]: !m[title] }));

  return (
    <>
      {/* Mobile backdrop — appears below the drawer, taps dismiss */}
      <div
        onClick={onCloseMobile}
        className={`md:hidden fixed inset-0 bg-slate-950/60 backdrop-blur-sm z-40 transition-opacity duration-200 ${
          mobileOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        data-testid="sidebar-backdrop"
        aria-hidden={!mobileOpen}
      />
      <aside
        className={`
          flex flex-col bg-slate-950 text-slate-100 border-r border-slate-900
          fixed md:sticky top-0 left-0 h-screen w-72 md:w-64 flex-shrink-0 z-50
          transition-transform duration-200 ease-out
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0
        `}
        data-testid="sidebar"
        role="navigation"
        aria-label="Main navigation"
      >
      <div className="px-5 py-5 flex items-center gap-3 border-b border-slate-900">
        <div className="h-9 w-9 rounded-md bg-white flex items-center justify-center">
          <XentraIcon className="h-7 w-7" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading text-base font-bold tracking-tight leading-none">
            XENTRA
          </div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-blue-400 mt-1 font-mono">
            CRM
          </div>
        </div>
        {/* Mobile close button */}
        <button
          type="button"
          onClick={onCloseMobile}
          className="md:hidden -mr-1 p-1 rounded text-slate-400 hover:text-white hover:bg-slate-900"
          aria-label="Close navigation"
          data-testid="sidebar-close-btn"
        >
          <XIcon className="h-5 w-5" />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-3">
        {groups.map((group, gi) => {
          if (!group.title) {
            // Ungrouped top items (Dashboard, Budget Tracker) — always visible
            return (
              <div key={gi} className="space-y-1">
                {group.items.map(({ to, label, icon: Icon, testid }) => (
                  <NavLink
                    key={to}
                    to={to}
                    data-testid={testid}
                    end={to === "/app/dashboard"}
                    className={({ isActive }) =>
                      `sidebar-link ${isActive ? "active" : ""}`
                    }
                  >
                    <Icon className="h-4 w-4 flex-shrink-0" />
                    <span>{label}</span>
                  </NavLink>
                ))}
              </div>
            );
          }

          const isOpen = openMap[group.title] !== false;
          const visibleItems = group.items.filter((it) => !it.adminOnly || isAdmin);
          if (visibleItems.length === 0) return null;
          return (
            <div key={gi} className="space-y-1">
              <button
                type="button"
                onClick={() => toggle(group.title)}
                data-testid={`nav-group-${group.title.toLowerCase()}`}
                aria-expanded={isOpen}
                className="w-full flex items-center justify-between px-2 pt-1 pb-2 text-[10px] uppercase tracking-[0.18em] text-slate-500 hover:text-slate-300 font-mono transition-colors cursor-pointer"
              >
                <span>{group.title}</span>
                <ChevronDown
                  className={`h-3 w-3 flex-shrink-0 transition-transform duration-200 ${
                    isOpen ? "rotate-0" : "-rotate-90"
                  }`}
                />
              </button>
              <div
                className={`overflow-hidden transition-all duration-200 ${
                  isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                }`}
              >
                <div className="space-y-1">
                  {visibleItems.map(({ to, label, icon: Icon, testid }) => (
                    <NavLink
                      key={to}
                      to={to}
                      data-testid={testid}
                      className={({ isActive }) =>
                        `sidebar-link ${isActive ? "active" : ""}`
                      }
                    >
                      <Icon className="h-4 w-4 flex-shrink-0" />
                      <span>{label}</span>
                    </NavLink>
                  ))}
                </div>
              </div>
            </div>
          );
        })}

        {isAdmin && (
          <div className="space-y-1 pt-2 border-t border-slate-900">
            <div className="px-2 pt-3 pb-2 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-mono">
              Administration
            </div>
            {adminItems.map(({ to, label, icon: Icon, testid }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={testid}
                className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
              >
                <Icon className="h-4 w-4 flex-shrink-0" />
                <span>{label}</span>
              </NavLink>
            ))}
          </div>
        )}
      </nav>

      <div className="p-4 border-t border-slate-900 text-[11px] text-slate-500 font-mono">
        <div className="flex items-center gap-2">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 pulse-dot" />
          All systems online
        </div>
        <div className="mt-1 text-slate-600">v0.1 · MVP</div>
      </div>
    </aside>
    </>
  );
}
